import React, { useState, useEffect } from 'react';
import { Modal, Typography, Button, Input } from '@crisellevaldez/speculo-ui';
import { Organization } from '../../../types/organization.types';
import { useSpeculoOAuthOperations } from '../../../hooks/oauth/useSpeculoOAuthOperations';
import { Key, CheckCircle2, AlertCircle, RotateCw, Copy } from 'lucide-react';

interface SpeculoOAuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedOrg?: Organization;
  onSuccess?: () => void;
}

export const SpeculoOAuthModal: React.FC<SpeculoOAuthModalProps> = ({
  isOpen,
  onClose,
  selectedOrg,
  onSuccess,
}) => {
  const {
    clientInfo,
    createOAuthClient,
    refreshOAuthToken,
    fetchClientInfo,
    isCreatingClient,
    isRefreshing,
    isFetchingInfo,
  } = useSpeculoOAuthOperations();

  const [copiedToken, setCopiedToken] = useState(false);
  const [newAccessToken, setNewAccessToken] = useState<string | null>(null);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedToken(true);
    setTimeout(() => setCopiedToken(false), 2000);
  };

  const handleCreateClient = async () => {
    const result = await createOAuthClient(selectedOrg?.id);
    if (result?.accessToken) {
      setNewAccessToken(result.accessToken);
    }
    if (onSuccess) {
      onSuccess();
    }
  };

  const handleClose = () => {
    setNewAccessToken(null);
    onClose();
  };

  const handleRefreshToken = async () => {
    const result = await refreshOAuthToken(selectedOrg?.id);
    if (result?.accessToken) {
      setNewAccessToken(result.accessToken);
    }
    if (onSuccess) {
      onSuccess();
    }
  };

  // Check OAuth status from API on modal open (oauthSetup in GQL may be stale/null)
  useEffect(() => {
    if (isOpen && selectedOrg?.id && !clientInfo) {
      fetchClientInfo(selectedOrg.id);
    }
  }, [isOpen, selectedOrg?.id]);

  const isConfigured = Boolean(
    clientInfo?.clientId ||
    selectedOrg?.oauthSetup?.created_at || selectedOrg?.oauthSetup?.createdAt ||
    selectedOrg?.oauthSetup?.clientId || selectedOrg?.oauthSetup?.client_id
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <Modal.Header>
        <Typography variant="h3">Speculo OAuth Management</Typography>
      </Modal.Header>
      <Modal.Body>
        <div className="space-y-6">
          <Typography variant="p" className="text-gray-600 dark:text-gray-400">
            Authentication credentials for connecting third-party integrations. Create an OAuth
            client before connecting integrations. Lost your token? Refresh it here.
          </Typography>

          {/* Single Combined Section */}
          <div className="border border-gray-200 dark:border-white/15 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <Typography variant="h5" className="font-semibold">
                OAuth Client
              </Typography>
              {isConfigured && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchClientInfo(selectedOrg?.id)}
                  disabled={isFetchingInfo}
                  className="flex items-center gap-2"
                >
                  <RotateCw className={`w-4 h-4 ${isFetchingInfo ? 'animate-spin' : ''}`} />
                  Check Status
                </Button>
              )}
            </div>

            {isFetchingInfo ? (
              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                <RotateCw className="w-4 h-4 animate-spin" />
                <span>Checking...</span>
              </div>
            ) : isConfigured ? (
              <div className="space-y-7">
                <div className="flex items-center gap-2 text-green-600 mb-3">
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="font-medium">Configured</span>
                </div>

                {/* Show access token when newly generated */}
                {newAccessToken && (
                  <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700/30 rounded-lg">
                    <div className="flex items-start gap-3 mb-4">
                      <div className="flex-1">
                        <p className="text-base font-semibold text-blue-900 dark:text-blue-300 mb-2">
                          Save Your Access Token
                        </p>
                        <p className="text-sm text-blue-800 dark:text-blue-400">
                          Copy and store this token securely. If you lose it, you can refresh to
                          generate a new one.
                        </p>
                      </div>
                    </div>

                    <div className="w-full">
                      <label className="text-sm font-medium text-gray-900 dark:text-gray-100 block mb-2">
                        Access Token
                      </label>
                      <div className="flex items-stretch gap-2 w-full">
                        <div className="flex-1 min-w-0">
                          <Input
                            value={newAccessToken}
                            readOnly
                            type="password"
                            className="font-mono text-sm bg-white dark:bg-white/5 w-full"
                          />
                        </div>
                        <Button
                          variant="outline"
                          onClick={() => handleCopy(newAccessToken)}
                          className="flex items-center gap-2 whitespace-nowrap"
                        >
                          {copiedToken ? (
                            <>
                              <CheckCircle2 className="w-5 h-5" />
                              <span>Copied!</span>
                            </>
                          ) : (
                            <>
                              <Copy className="w-5 h-5" />
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-red-600 mb-2">
                  <AlertCircle className="w-5 h-5" />
                  <span className="font-semibold text-sm">Not Configured</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex justify-end w-full">
          {isConfigured ? (
            <Button
              variant="primary"
              onClick={handleRefreshToken}
              isLoading={isRefreshing}
              disabled={isRefreshing}
              className="flex items-center gap-2 h-8"
            >
              <RotateCw className="w-4 h-4" />
              Refresh Token
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={handleCreateClient}
              isLoading={isCreatingClient}
              disabled={isCreatingClient}
              className="flex items-center gap-2 h-8"
            >
              <Key className="w-4 h-4" />
              Create OAuth Client
            </Button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
};
